import { log } from './logger'
import { recordTrace } from './perf'

// Per route visit: how long taps took to paint (Event Timing) and how much the
// main thread was blocked (Long Animation Frames, or Long Tasks where LoAF is
// missing). Chromium has all three — Android's WebView included; WebKit has
// none, so iOS visits record nothing here.

interface LongFrameEntry extends PerformanceEntry {
  blockingDuration?: number
}

const SLOW_INTERACTION_MS = 200
const MIN_VISIT_MS = 1000
const MAX_SLOW_LOGS_PER_VISIT = 5

function supports(type: string): boolean {
  return typeof PerformanceObserver !== 'undefined' && (PerformanceObserver.supportedEntryTypes ?? []).includes(type)
}

const hasEventTiming = supports('event')
const frameType = supports('long-animation-frame') ? 'long-animation-frame' : supports('longtask') ? 'longtask' : null

interface Visit {
  route: string
  startWall: number
  startPerf: number
  interactions: Map<number, number>
  longFrames: number
  blockingMs: number
  worstFrameMs: number
  slowLogged: number
}

let visit: Visit | null = null
let lastRoute: string | null = null

function newVisit(route: string): Visit {
  return {
    route,
    startWall: Date.now(),
    startPerf: performance.now(),
    interactions: new Map(),
    longFrames: 0,
    blockingMs: 0,
    worstFrameMs: 0,
    slowLogged: 0,
  }
}

// web-vitals' INP: the worst interaction, ignoring one outlier per 50.
function inp(latencies: number[]): number {
  const sorted = [...latencies].sort((a, b) => b - a)
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length / 50))]
}

function flush() {
  const v = visit
  visit = null
  if (!v) return
  const duration = performance.now() - v.startPerf
  if (duration < MIN_VISIT_MS) return
  const metrics: Record<string, number> = {}
  if (frameType) {
    metrics.long_frames = v.longFrames
    metrics.blocking_ms = Math.round(v.blockingMs)
    metrics.worst_frame_ms = Math.round(v.worstFrameMs)
  }
  if (hasEventTiming) {
    const latencies = [...v.interactions.values()]
    metrics.interactions = latencies.length
    metrics.slow_interactions = latencies.filter(ms => ms >= SLOW_INTERACTION_MS).length
    if (latencies.length) metrics.inp_ms = Math.round(inp(latencies))
  }
  recordTrace('route_responsiveness', v.startWall, duration, { route: v.route }, metrics)
}

function describe(target: EventTarget | null): string {
  if (!(target instanceof Element)) return '?'
  const label = target.getAttribute('data-testid') ?? target.getAttribute('aria-label') ?? target.classList[0]
  return label ? `${target.tagName.toLowerCase()}[${label.slice(0, 40)}]` : target.tagName.toLowerCase()
}

function onEvent(entries: PerformanceEventTiming[]) {
  if (!visit) return
  for (const e of entries) {
    if (!e.interactionId) continue
    const prev = visit.interactions.get(e.interactionId) ?? 0
    if (e.duration <= prev) continue
    visit.interactions.set(e.interactionId, e.duration)
    if (e.duration >= SLOW_INTERACTION_MS && prev < SLOW_INTERACTION_MS && visit.slowLogged < MAX_SLOW_LOGS_PER_VISIT) {
      visit.slowLogged++
      log('perf', `slow ${e.name} ${Math.round(e.duration)}ms`, {
        route: visit.route,
        target: describe(e.target),
        input_delay_ms: Math.round(e.processingStart - e.startTime),
        processing_ms: Math.round(e.processingEnd - e.processingStart),
      })
    }
  }
}

function onFrame(entries: LongFrameEntry[]) {
  if (!visit) return
  for (const e of entries) {
    visit.longFrames++
    visit.blockingMs += e.blockingDuration ?? Math.max(0, e.duration - 50)
    visit.worstFrameMs = Math.max(visit.worstFrameMs, e.duration)
  }
}

let started = false

function start() {
  if (started || (!hasEventTiming && !frameType)) return
  started = true
  if (hasEventTiming) {
    new PerformanceObserver(list => onEvent(list.getEntries() as PerformanceEventTiming[]))
      .observe({ type: 'event', durationThreshold: 16 } as PerformanceObserverInit)
  }
  if (frameType) {
    new PerformanceObserver(list => onFrame(list.getEntries() as LongFrameEntry[])).observe({ type: frameType })
  }
  // A backgrounded app may never navigate again, so a visit ends when it's hidden.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush()
    else if (lastRoute) visit = newVisit(lastRoute)
  })
}

export function startRouteVisit(route: string) {
  start()
  if (!started) return
  flush()
  lastRoute = route
  if (document.visibilityState === 'visible') visit = newVisit(route)
}
